import type { RawUiConfig, UiConfig, UiPermissions, UiSettings, UiStyles, UiVisibility } from './DefaultConfig';
import type { GatekeeperClient } from '@easterngraphics/gatekeeper-lib';

import { ArticleNoInSubInfo, EnableMetatypeProperties, ManufacturerInSubInfo, SeriesInSubInfo, showArticleName } from '../DefaultValues';

import { normalizeUiConfig, ArticleDrawerVariant, PropertyEditorVariant } from './DefaultConfig';

import { USER_LANGUAGES } from '@egr/xbox/base-app/i18n';
import { DATA_LANGUAGES } from '@egr/xbox/base-app/managers/SessionManager';
import { withDebugTimer } from '@egr/xbox/utils/Debug';
import { getEnvBoolean, getEnvNumber, getEnvVar, getEnvValue } from '@egr/xbox/utils/ReactScriptHelper';
import { inIframe, isMobileDevice } from '@egr/xbox/utils/UserAgent';

import { contains } from '@easterngraphics/wcf/modules/utils/array';
import { ajax } from '@easterngraphics/wcf/modules/utils/async';
import { loadStyleSheet } from '@easterngraphics/wcf/modules/utils/dom';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const FORBIDDEN_KEYS = ['customFields', 'HOOK_URL'];

export async function loadUiSettings(gatekeeperId: string | undefined, gateKeeperClient: GatekeeperClient): Promise<UiConfig | undefined> {
    try {
        if (isNullOrEmpty(gatekeeperId)) {
            return undefined;
        }
        let settingsId: string = gatekeeperId;
        if (settingsId.indexOf(':') !== -1) {
            settingsId = settingsId.split(':')[0];
        }
        const rawUiConfig = await withDebugTimer<RawUiConfig>(
            'ui settings request',
            (): Promise<RawUiConfig> => {
                return gateKeeperClient.getSettings<RawUiConfig>(getEnvVar('SETTINGS_FOLDER', 'pCon_ui'), settingsId);
            }
        );

        const normalizedUiConfig = normalizeUiConfig(rawUiConfig);
        const uiConfig = await getUIManagerStateWithOverrides(normalizedUiConfig);

        uiConfig.styles = await initializeStyles(normalizedUiConfig.styles);

        return uiConfig;
    } catch {
        return undefined;
    }
}

export async function sendParams() {
    const sendParamsUrl = getEnvVar('SEND_PARAMS_URL');
    if (!sendParamsUrl) {
        return 'ERROR';
    }

    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);

    FORBIDDEN_KEYS.forEach((key) => {
        params.delete(key);
    });

    for (const key of [...params.keys()]) {
        if (!/^[A-Za-z0-9_-]+$/.test(key)) params.delete(key);
    }
    try {
        return ajax<string>(
            'POST',
            sendParamsUrl,
            new URLSearchParams({ hash: params.toString() }).toString(),
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            }
        );
    } catch (error) {
        console.warn(error);
        return 'ERROR';
    }
}

async function initializeStyles(styles: UiStyles): Promise<UiStyles> {
    const paletteImport = styles.paletteImport;

    if (isNotNullOrEmpty(paletteImport)) {
        try {
            const paletteOverride: UiStyles['paletteOverride'] = await withDebugTimer<UiStyles['paletteOverride']>(
                'load palette json',
                (): Promise<UiStyles['paletteOverride']> => {
                    return ajax<UiStyles['paletteOverride']>(
                        'GET',
                        paletteImport,
                        null,
                        {
                            responseType: 'json',
                            headers: {
                                'Content-Type': 'application/json; charset=utf-8'
                            }
                        }
                    );
                }
            );
            styles.paletteOverride = paletteOverride;
        } catch {
            console.warn('palette import failed');
        }
    }

    if (inIframe()) {
        const cssImport: string | undefined = styles.cssImport;
        if (isNotNullOrEmpty(cssImport)) {
            try {
                await loadStyleSheet(cssImport);
            } catch {
                console.warn('css import failed');
            }
        }
    }

    return styles;
}

function getUISettings(uiConfig: UiConfig): UiConfig['settings'] {
    const settings: UiSettings = uiConfig.settings;

    let lang: string = getEnvVar('lang', settings.general.lang ?? 'en');
    if (lang === 'auto') {
        lang = navigator.language ? navigator.language.substring(0, 2) : 'en';
    }

    settings.general.lang = contains(USER_LANGUAGES, lang) ? lang : 'en';

    const ofmlLang: string = getEnvVar('ofmlLang', settings.general.ofmlLang ?? settings.general.lang);
    settings.general.ofmlLang = contains(DATA_LANGUAGES, ofmlLang) ? ofmlLang : settings.general.lang;

    if (!uiConfig.permissions.pricing) {
        settings.pricing.showPrice = false;
    } else {
        settings.pricing.showPrice = getEnvBoolean('sp', true) !== getEnvBoolean('sp', false) ? settings.pricing.showPrice : getEnvBoolean('sp', true);
    }

    settings.general.fullscreenOnly = getEnvBoolean('fso', settings.general.fullscreenOnly);
    settings.general.generateContactRequestPdf = getEnvBoolean(
        'gpdf',
        settings.general.generateContactRequestPdf ?? settings.general.generateContactRequestPdf
    );

    if (isNotNullOrEmpty(settings.general.propertyDefinitionUrl)) {
        settings.general.propertyServiceUrl = getEnvVar('DEFAULT_PROPSERVICE_URL');
    }

    settings.pricing.tax = getEnvVar('tax', settings.pricing.tax);

    if (isNotNullOrEmpty(settings.pricing.priceServiceUrl)) {
        settings.pricing.calculationScheme = 'STD_PUI';
    }

    // configuration camera settings
    settings.camera.lockToTarget = getEnvBoolean('ltt', settings.camera.lockToTarget);
    settings.camera.restrictRotationToZAxis = getEnvBoolean('rrz', settings.camera.restrictRotationToZAxis);
    settings.camera.autorotate = isMobileDevice ? false : getEnvBoolean('are', settings.camera.autorotate);
    settings.camera.autorotationDelay = getEnvNumber('ard', settings.camera.autorotationDelay ?? 5000);
    settings.camera.autorotationSpeed = getEnvNumber('ars', settings.camera.autorotationSpeed ?? 15);

    settings.camera.disableControls = getEnvBoolean('dcc', settings.camera.disableControls);
    if (settings.camera.disableControls) {
        settings.camera.lockToTarget = true;
        settings.camera.restrictRotationToZAxis = true;
        settings.camera.autorotate = false;
    }

    const cameraAlpha: number | undefined = getEnvNumber('alpha');
    const cameraBeta: number | undefined = getEnvNumber('beta');
    const cameraFov: number | undefined = getEnvNumber('fov');

    if (cameraAlpha != null && cameraBeta != null) {
        settings.camera.alpha = cameraAlpha;
        settings.camera.beta = cameraBeta;
        settings.camera.fov = cameraFov;
    }

    return settings;
}

async function getUIManagerStateWithOverrides(uiConfig: UiConfig): Promise<UiConfig> {
    const permissions: UiPermissions = uiConfig.permissions;
    const visibility: UiVisibility = uiConfig.visibility;
    const styles: UiStyles = uiConfig.styles;

    uiConfig.defaults = getDefaults(uiConfig);
    uiConfig.settings = getUISettings(uiConfig);

    visibility.general.header = getEnvBoolean('sh', visibility.general.header);

    permissions.iframeAllowedParents.push('*.localhost', '*.pcon-solutions.com', '*.easterngraphics.com');
    permissions.iframeAllowedParents = permissions.iframeAllowedParents.map((value) => {
        return value.toLocaleLowerCase();
    });

    // configuration layout
    const backgroundFallback: UiStyles['background'] = getEnvBoolean('rb', false) ? 'radiantgrey' : styles.background;
    styles.background = getEnvVar('sb', backgroundFallback) as UiStyles['background'];

    styles.articleDrawerVariant = getEnvValue<ArticleDrawerVariant>(
        'adv',
        [ArticleDrawerVariant.SINGLE, ArticleDrawerVariant.MODULAR],
        styles.articleDrawerVariant ?? ArticleDrawerVariant.SINGLE
    );
    styles.propertyEditorVariant = getEnvValue<PropertyEditorVariant>(
        'pev',
        [PropertyEditorVariant.FULL, PropertyEditorVariant.COMPACT],
        styles.propertyEditorVariant ?? PropertyEditorVariant.FULL
    );
    visibility.articleData = getArticleData(visibility);

    if (uiConfig.settings.camera.disableControls) {
        (Object.keys(visibility.buttons) as Array<keyof typeof visibility.buttons>).forEach((key) => {
            visibility.buttons[key] = visibility.buttons[key].filter((button) => !button.startsWith('view'));
        });
    }

    uiConfig.permissions = permissions;
    uiConfig.visibility = visibility;
    return uiConfig;
}

function getDefaults(uiConfig: UiConfig): UiConfig['defaults'] {
    const { settings, visibility, permissions } = uiConfig;

    return {
        lang: settings.general.lang,
        fullscreenOnly: settings.general.fullscreenOnly,
        showPrice: settings.pricing.showPrice && permissions.pricing,
        netPrice: settings.pricing.netPrice,
        header: visibility.general.header,
        disableCameraControls: settings.camera.disableControls,
        lockToTarget: settings.camera.lockToTarget,
        autorotate: settings.camera.autorotate,
        restrictRotationToZAxis: settings.camera.restrictRotationToZAxis,
        description: true,
        manufacturerInSubInfo: visibility.articleData.manufacturerInSubInfo,
        seriesInSubInfo: visibility.articleData.seriesInSubInfo,
        articleNoInSubInfo: visibility.articleData.articleNoInSubInfo,
        showArticleName: visibility.articleData.showArticleName,
        showPropertyHeaders: visibility.articleData.showPropertyHeaders,
        showUneditableProperties: visibility.articleData.showUneditableProperties,
        enableMetatypeProperties: visibility.articleData.enableMetatypeProperties
    };
}

function getArticleData(visibility: UiVisibility): UiVisibility['articleData'] {
    const articleData = visibility.articleData;
    articleData.showPropertyHeaders = getEnvBoolean('sph', visibility.articleData.showPropertyHeaders);
    articleData.showUneditableProperties = getEnvBoolean('sup', visibility.articleData.showUneditableProperties);
    articleData.description = !getEnvBoolean('hde' /* hide description */, !visibility.articleData.description);

    articleData.manufacturerInSubInfo = getEnvBoolean(
        'msi',
        visibility.articleData.manufacturerInSubInfo ?? ManufacturerInSubInfo
    );

    articleData.seriesInSubInfo = getEnvBoolean('ssi', visibility.articleData.seriesInSubInfo ?? SeriesInSubInfo);
    articleData.articleNoInSubInfo = getEnvBoolean('asi', visibility.articleData.articleNoInSubInfo ?? ArticleNoInSubInfo);
    articleData.showArticleName = getEnvBoolean('san', visibility.articleData.showArticleName ?? showArticleName);

    articleData.enableMetatypeProperties = getEnvBoolean(
        'emp',
        articleData.enableMetatypeProperties ?? EnableMetatypeProperties
    );

    return articleData;
}

export type ButtonTypes =
    'dimension' | 'snapshot' | 'fullscreen' | 'iso' | 'cad' | 'pdf' | 'interactors' | 'ar' |
    'viewFront' | 'viewTop' | 'viewLeft' | 'viewRight';